import {
  createInventoryPlanner,
  getExternalSystems,
  getInventoryPlanner,
  getXero,
  resetXero,
  setLinkedAccounts,
  setLinkedTaxRates,
  setOperations,
  setSyncHistory,
  setSyncSchedule,
  updateExternalSystemInOperations,
  updateExternalSystemOperationOptedIn,
  updateIntegrationStatusByCode,
  updateInventoryPlanner,
} from "../slice/integrationSlice";
import { apiSlice } from "./apiSlice";

export const integrationApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    createExtSysCnf: builder.mutation({
      query: data => ({ url: "/api_integration/create_ext_sys_cnf/", method: "POST", body: data }),
      onQueryStarted: ({ external_system }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(_ => dispatch(updateExternalSystemInOperations(external_system)))
          .catch(_ => {});
      },
    }),
    createInventoryPlannerAccount: builder.mutation({
      query: data => ({ url: "/api_integration/inventory_planner/", method: "POST", body: data }),
      onQueryStarted: (result, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(_ => dispatch(createInventoryPlanner(result.data))).catch(_ => {});
      },
    }),
    createSyncSchedule: builder.mutation({
      query: data => ({ url: "/api_integration/sync_schedule/", method: "POST", body: data }),
    }),
    bulkXeroAccountMapping: builder.mutation({
      query: data => ({
        url: "/api_account/account_mapping/xero_mapping/",
        method: "POST",
        body: data,
      }),
    }),
    bulkTaxRateXeroMapping: builder.mutation({
      query: data => ({
        url: "/api_tax/tax_rate_mapping/xero_mapping/",
        method: "POST",
        body: data,
      }),
    }),
    createExtSysOprCnf: builder.mutation({
      query: data => ({
        url: "/api_integration/create_ext_sys_opr_cnf/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: ({ external_system_operation }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(_ => dispatch(updateExternalSystemOperationOptedIn(external_system_operation)))
          .catch(_ => {});
      },
    }),
    deleteInventoryPlanner: builder.mutation({
      query: () => ({ url: "/api_integration/inventory_planner/", method: "DELETE" }),
    }),
    deleteXeroIntegration: builder.mutation({
      query: () => ({ url: "/api_integration/xero/", method: "DELETE" }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(_ => dispatch(resetXero())).catch(error => {});
      },
    }),
    getExternalSystems: builder.query({
      query: () => "/api_integration/external_system/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getExternalSystems(result.data))).catch(error => {});
      },
    }),
    getIntegrationExternalSystems: builder.query({
      query: () => "/api_integration/ext_sys/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getExternalSystems(result.data))).catch(error => {});
      },
    }),

    getIntegrationSyncSchedule: builder.query({
      query: id => `/api_integration/sync_schedule/?config_id=${id}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setSyncSchedule(result.data))).catch(error => {});
      },
    }),

    getInventoryPlanner: builder.query({
      query: () => "/api_integration/inventory_planner/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getInventoryPlanner(result.data)))
          .catch(error => {});
      },
    }),
    getXeroConfig: builder.query({
      query: () => "/api_integration/xero/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getXero(result.data))).catch(error => {});
      },
    }),
    getExternalSystemByCode: builder.query({
      query: code => `/api_integration/external_system/${code}/`,
    }),
    getOperations: builder.query({
      query: params =>
        params ? `/api_integration/available_opr/?${params}` : "/api_integration/available_opr/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setOperations(result.data))).catch(error => {});
      },
    }),
    // Step 2 searchable select may pass undefined or `{ params }`.
    // Never destructure a bare null — that emptied the Xero account dropdowns.
    getXeroAccounts: builder.query({
      query: arg => {
        let queryParams;
        if (arg == null) {
          queryParams = undefined;
        } else if (typeof arg === "object" && arg !== null && "params" in arg) {
          queryParams = arg.params;
        } else if (typeof arg === "string") {
          queryParams = arg;
        }
        return queryParams
          ? `/api_integration/xero_connector/connector/accounts/?${queryParams}`
          : "/api_integration/xero_connector/connector/accounts/";
      },
    }),
    getXeroTaxRates: builder.query({
      query: params =>
        params
          ? `/api_integration/xero_connector/connector/tax_rates/?${params}`
          : "/api_integration/xero_connector/connector/tax_rates/",
    }),
    getLinkedAccounts: builder.query({
      query: params =>
        params ? `/api_account/account_mapping/?${params}` : "/api_account/account_mapping/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setLinkedAccounts(result.data));
          })
          .catch(error => {});
      },
    }),
    getLinkedTaxRates: builder.query({
      query: params =>
        params ? `/api_tax/tax_rate_mapping/?${params}` : "/api_tax/tax_rate_mapping/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setLinkedTaxRates(result.data));
          })
          .catch(error => {});
      },
    }),
    getSyncHistory: builder.query({
      query: params =>
        params ? `/api_integration/sync_logs/?${params}` : "/api_integration/sync_logs/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setSyncHistory(result.data));
          })
          .catch(error => {});
      },
    }),
    updateIntegrationStatusByCode: builder.mutation({
      query: ({ data, params }) => ({
        url: `/api_integration/${params}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: ({ params }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result =>
            dispatch(updateIntegrationStatusByCode({ result: result.data, code: params })),
          )
          .catch(_ => {});
      },
    }),
    updateInventoryPlanner: builder.mutation({
      query: data => ({
        url: "/api_integration/inventory_planner/",
        method: "PUT",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateInventoryPlanner(result.data))).catch(_ => {});
      },
    }),
  }),
});

export const {
  useBulkTaxRateXeroMappingMutation,
  useBulkXeroAccountMappingMutation,
  useCreateInventoryPlannerAccountMutation,
  useCreateExtSysCnfMutation,
  useCreateExtSysOprCnfMutation,
  useCreateSyncScheduleMutation,
  useDeleteInventoryPlannerMutation,
  useDeleteXeroIntegrationMutation,
  useGetExternalSystemByCodeQuery,
  useGetIntegrationExternalSystemsQuery,
  useGetExternalSystemsQuery,
  useGetInventoryPlannerQuery,
  useGetXeroAccountsQuery,
  useGetLinkedAccountsQuery,
  useGetLinkedTaxRatesQuery,
  useGetIntegrationSyncScheduleQuery,
  useGetXeroConfigQuery,
  useGetXeroTaxRatesQuery,
  useGetSyncHistoryQuery,
  useUpdateIntegrationStatusByCodeMutation,
  useUpdateInventoryPlannerMutation,
  useGetOperationsQuery,
} = integrationApiSlice;
